import PersonIcon from "@mui/icons-material/Person";
import Pen from "assets/img/svg/pen";
import Plus from "assets/img/svg/plus";
import type { IBeneficiar, IBeneficiaryHeir } from "containers/Beneficiars/types";
import { formatLocaleDateString } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchBeneficiar, fetchHeirs, fetchTermAssociatedToBeneficiaries } from "store/beneficiaryStore";
import { useAppDispatch, useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import EmptyState from "../EmptyState";
import InformationGrid from "../InformationGrid";
import StyledButton from "../StyledButton";
import TextButton from "../TextButton";
import TitleSeparation from "../TitleSeparation";
import styles from "./style.module.scss";
import SuccessionModal from "./SuccessionModal";

interface IHeirs {
    beneficiary: IBeneficiar;
}

const fullName = (person: { surname?: string | null; name?: string | null }) =>
    `${person.surname?.toUpperCase() ?? ""} ${person.name ?? ""}`.trim();

/**
 * "Héritiers" block of the associated persons tab: lists the heirs of a deceased beneficiary
 * (and the successions this beneficiary inherits from), and opens the succession modal.
 */
const Heirs = ({ beneficiary }: IHeirs) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const ability = useAbility();

    const { heirs, heirsLoadStatus, heirsBeneficiaryId, heirOf, heirOfLoadStatus } = useAppSelector(
        (state) => state.beneficiary,
    );
    const [modalMode, setModalMode] = useState<"create" | "update" | null>(null);

    const isCurrentBeneficiary = heirsBeneficiaryId === beneficiary?.id;
    const isLoading =
        !isCurrentBeneficiary ||
        ["idle", "pending"].includes(heirsLoadStatus) ||
        ["idle", "pending"].includes(heirOfLoadStatus);
    // A beneficiary who only inherits (no heirs of their own) gets a bare "Heir of …" mention, not the heirs block.
    const inheritsOnly = !isLoading && heirOf.length > 0 && heirs.length === 0;
    const canCreate = ability.can("create", "BeneficiaryHeir");
    const canUpdate = ability.can("update", "BeneficiaryHeir");

    const handleClose = (saved: boolean) => {
        setModalMode(null);
        if (saved && beneficiary?.id) {
            dispatch(fetchBeneficiar(beneficiary.id));
            dispatch(fetchHeirs(beneficiary.id));
            dispatch(fetchTermAssociatedToBeneficiaries({ beneficiaryId: beneficiary.id }));
        }
    };

    const formatHeirData = (
        heir: Pick<IBeneficiaryHeir, "sharePercent"> &
            Partial<Pick<IBeneficiaryHeir, "inheritedTermsCount" | "createdAt">>,
    ) => ({
        [t("Heirs.Share")]: `${heir.sharePercent} %`,
        [t("Heirs.InheritedTerms")]: `${heir.inheritedTermsCount ?? 0}`,
        [t("Heirs.RecordedOn")]: heir.createdAt ? formatLocaleDateString({ date: heir.createdAt }) : "-",
    });

    return (
        <div className={styles["heirs"]}>
            <TitleSeparation
                title={t(inheritsOnly ? "Heirs.SuccessionTitle" : "Heirs.Title")}
                description={inheritsOnly ? undefined : t("Heirs.Subtitle")}
                titleStyle={{ fontSize: "14px" }}
                style={{ width: "100%", marginBottom: "0.75rem" }}
            />

            {!isLoading && heirOf.length > 0 && (
                <div className={styles["heir-of"]}>
                    {heirOf.map((link) => (
                        <TextButton
                            key={link.id}
                            text={t("Heirs.HeirOf", { name: fullName(link), share: link.sharePercent })}
                            handleClick={() => navigate(`/beneficiars/${link.deceasedBeneficiarId}`)}
                            underlineOnHover
                            style={{ fontSize: "0.8rem", color: variables["info"] }}
                        />
                    ))}
                </div>
            )}

            {isLoading ? (
                <InformationGrid
                    title=""
                    titleIcon={<PersonIcon style={{ color: variables["foreground"], width: "16px" }} />}
                    data={formatHeirData({ sharePercent: 0, inheritedTermsCount: 0 })}
                    isLoading
                    style={{ marginBottom: "1rem", width: "100%" }}
                />
            ) : heirs.length === 0 ? (
                !inheritsOnly && <EmptyState type="heirs" />
            ) : (
                heirs.map((heir) => (
                    <InformationGrid
                        key={heir.id}
                        title={fullName(heir)}
                        titleIcon={<PersonIcon style={{ color: variables["foreground"], width: "16px" }} />}
                        data={formatHeirData(heir)}
                        style={{ marginBottom: "1rem", width: "100%" }}
                        headerActions={[
                            <TextButton
                                key="see-heir"
                                text={t("Representatives.SeeDetail")}
                                handleClick={() => navigate(`/beneficiars/${heir.heirBeneficiarId}`)}
                                underlineOnHover
                                style={{ fontSize: "0.8rem", color: variables["foreground"] }}
                            />,
                        ]}
                    />
                ))
            )}

            {!isLoading && heirs.length === 0 && !inheritsOnly && canCreate && (
                <StyledButton
                    sx={{ gap: "8px", padding: "4px 16px", background: "white", margin: "0", display: "flex" }}
                    onClick={() => setModalMode("create")}
                    buttonType="secondary"
                    className={styles["heirs-action"]}
                >
                    <Plus />
                    {t("Heirs.RecordSuccession")}
                </StyledButton>
            )}
            {!isLoading && heirs.length > 0 && canUpdate && (
                <StyledButton
                    sx={{ gap: "8px", padding: "4px 16px", background: "white", margin: "0", display: "flex" }}
                    onClick={() => setModalMode("update")}
                    buttonType="secondary"
                    className={styles["heirs-action"]}
                >
                    <Pen width={14} />
                    {t("Heirs.EditRepartition")}
                </StyledButton>
            )}

            {modalMode && (
                <SuccessionModal
                    open
                    mode={modalMode}
                    beneficiary={beneficiary}
                    existingHeirs={heirs}
                    onClose={handleClose}
                />
            )}
        </div>
    );
};

export default Heirs;
